import axios from "axios";
import { api } from "../config";
import { getAuthHeaders } from "./api_helper";

/**
 * An HTTP client that keeps the server's answer when a request fails.
 *
 * api_helper installs a global response interceptor that rejects with a plain
 * STRING — Promise.reject(message) — which discards the response. For a form
 * that only wants a sentence to display that is fine. It is not fine when the
 * caller needs to tell one failure from another: the status code is gone, so is
 * the error code, and a catch block written as `e.message || fallback` shows the
 * fallback every time, because a string has no `.message`. Billing failed that
 * way — every cause, from "no subscription" to "Stripe is not configured",
 * surfaced as one generic sentence.
 *
 * Interceptors registered on the default axios instance do not apply to ones
 * made with create(), so this gets the real response.
 *
 * Success resolves to the response BODY, matching what the interceptor does, so
 * a caller can be switched over without touching its success path.
 *
 * Failure throws a real Error carrying:
 *   message  the server's own words, preferring `message` over `error`
 *   status   the HTTP status, or null if the request never got a reply
 *   code     the machine-readable error code, when the body carries one
 *   body     everything the server sent, for anything else a caller needs
 */

// baseURL is read from config rather than inherited: create() copies
// axios.defaults once, and whether api_helper has run by then depends on import
// order.
const client = axios.create({ baseURL: api.API_URL });

const toError = (err) => {
  const response = err?.response;
  const body = (response && response.data) || {};
  const message =
    (typeof body === "object" && (body.message || body.error))
    || err?.message
    || "The request failed.";
  const error = new Error(String(message));
  error.status = response?.status ?? null;
  // The API names its machine-readable code two ways: `error` on the billing
  // routes, `code` on the ones that return { ok: false, code, message }. Both
  // are read, so a caller can branch on the code without knowing which handler
  // it happened to reach.
  error.code =
    (typeof body?.error === "string" && body.error)
    || (typeof body?.code === "string" && body.code)
    || null;
  error.body = body;
  return error;
};

// The token is attached per request rather than at creation: a member may sign
// in after this module loads.
const config = (extra = {}) => ({
  ...extra,
  headers: { ...getAuthHeaders(), ...(extra.headers || {}) },
});

export const get = async (url, params) => {
  try {
    const res = await client.get(url, config({ params }));
    return res.data;
  } catch (err) {
    throw toError(err);
  }
};

export const post = async (url, data) => {
  try {
    const res = await client.post(url, data, config());
    return res.data;
  } catch (err) {
    throw toError(err);
  }
};

export const put = async (url, data) => {
  try {
    const res = await client.put(url, data, config());
    return res.data;
  } catch (err) {
    throw toError(err);
  }
};

export default { get, post, put };
